/**
 * Per-order carrier shipping-label status from team attaches (Printing + Packers).
 */

import { getQueueOrderKey } from '../services/printQueueModel';
import { SHIPPING_LABEL_ATTACH_STATUS } from '../services/shippingLabelAttachService';

export const SHIP_LABEL_ORDER_STATUS = Object.freeze({
  ATTACHED: 'attached',
  NEEDS_CHECK: 'needs_check',
  MISSING: 'missing',
});

/**
 * All identity keys that may appear on an attach page.orderKey.
 * @param {object} order
 * @returns {string[]}
 */
export function collectShipLabelOrderKeys(order) {
  if (!order || typeof order !== 'object') return [];
  const raw = [
    getQueueOrderKey(order),
    order.source_id,
    order.sourceId,
    order.orderId,
    order.order_id,
    order.orderNumber,
    order.order_number,
    order.id,
  ];
  return Array.from(
    new Set(
      raw
        .map((value) => String(value || '').replace(/^#/, '').trim())
        .filter(Boolean),
    ),
  );
}

/**
 * @param {object[]} attaches
 * @returns {Map<string, { status: string, pageCount: number, attachIds: string[] }>}
 */
export function buildShipLabelOrderStatusMap(attaches = []) {
  /** @type {Map<string, { attached: boolean, pending: boolean, pageCount: number, attachIds: Set<string> }>} */
  const scratch = new Map();

  const touch = (orderKey, status, attachId, pageCountDelta = 1) => {
    const key = String(orderKey || '').replace(/^#/, '').trim();
    if (!key) return;
    if (!scratch.has(key)) {
      scratch.set(key, {
        attached: false,
        pending: false,
        pageCount: 0,
        attachIds: new Set(),
      });
    }
    const entry = scratch.get(key);
    if (status === SHIPPING_LABEL_ATTACH_STATUS.ATTACHED) entry.attached = true;
    if (status === SHIPPING_LABEL_ATTACH_STATUS.PENDING_REVIEW) entry.pending = true;
    entry.pageCount += pageCountDelta;
    if (attachId) entry.attachIds.add(String(attachId));
  };

  (attaches || []).forEach((attach) => {
    const status = attach?.status;
    if (
      status !== SHIPPING_LABEL_ATTACH_STATUS.ATTACHED &&
      status !== SHIPPING_LABEL_ATTACH_STATUS.PENDING_REVIEW
    ) {
      return;
    }
    (attach.pages || []).forEach((page) => {
      touch(page?.orderKey, status, attach.id, 1);
    });
  });

  /** @type {Map<string, { status: string, pageCount: number, attachIds: string[] }>} */
  const map = new Map();
  scratch.forEach((entry, key) => {
    let status = SHIP_LABEL_ORDER_STATUS.MISSING;
    if (entry.attached) status = SHIP_LABEL_ORDER_STATUS.ATTACHED;
    else if (entry.pending) status = SHIP_LABEL_ORDER_STATUS.NEEDS_CHECK;
    map.set(key, {
      status,
      pageCount: entry.pageCount,
      attachIds: Array.from(entry.attachIds),
    });
  });
  return map;
}

/**
 * @param {object} order
 * @param {Map<string, { status: string, pageCount: number, attachIds: string[] }>} statusMap
 */
export function resolveShipLabelOrderStatus(order, statusMap) {
  const keys = collectShipLabelOrderKeys(order);
  if (!keys.length || !(statusMap instanceof Map)) {
    return {
      status: SHIP_LABEL_ORDER_STATUS.MISSING,
      pageCount: 0,
      attachIds: [],
      matchedKey: '',
    };
  }
  let best = null;
  let matchedKey = '';
  for (const key of keys) {
    const entry = statusMap.get(key);
    if (!entry) continue;
    if (entry.status === SHIP_LABEL_ORDER_STATUS.ATTACHED) {
      return { ...entry, matchedKey: key };
    }
    if (!best || entry.status === SHIP_LABEL_ORDER_STATUS.NEEDS_CHECK) {
      best = entry;
      matchedKey = key;
    }
  }
  if (best) return { ...best, matchedKey };
  return {
    status: SHIP_LABEL_ORDER_STATUS.MISSING,
    pageCount: 0,
    attachIds: [],
    matchedKey: '',
  };
}

export function shipLabelStatusLabel(status) {
  if (status === SHIP_LABEL_ORDER_STATUS.ATTACHED) return 'Ship label';
  if (status === SHIP_LABEL_ORDER_STATUS.NEEDS_CHECK) return 'Ship label?';
  return 'No ship label';
}

export function shipLabelStatusTitle(status) {
  if (status === SHIP_LABEL_ORDER_STATUS.ATTACHED) {
    return 'Carrier shipping label attached for this order. Open Wave desk to review.';
  }
  if (status === SHIP_LABEL_ORDER_STATUS.NEEDS_CHECK) {
    return 'A shipping-label page is linked but still needs a human check. Open Wave desk.';
  }
  return 'No aligned carrier shipping label yet. Open Wave desk to upload or match labels.';
}
